import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { SuccessNotify, ErrorNotify } from "../../shared/Alert";
import {
  createChecklist,
  updateChecklist,
  getChecklistDetail,
  getStandardTypes,
  getClientTypes,
  getWorkTypes,
  getIndustryTypes,
} from "../../helpers/backend_helper";
import { showLoading } from "react-global-loading";
import "../Standards/Standards.scss";

const asOptions = (records = []) =>
  records.map((item) => ({ label: item.name, value: item.id }));

const selectedOptions = (records = [], key) =>
  records
    .map((item) => {
      const related = item[key] || item;
      return { label: related.name, value: related.id };
    })
    .filter((item) => item.value);

const ChecklistDetail = ({ detailId, closeAction }) => {
  const [standardList, setStandardList] = useState([]);
  const [clientTypeOptions, setClientTypeOptions] = useState([]);
  const [workTypeOptions, setWorkTypeOptions] = useState([]);
  const [industryTypeOptions, setIndustryTypeOptions] = useState([]);
  const [selectedClientTypes, setSelectedClientTypes] = useState([]);
  const [selectedWorkTypes, setSelectedWorkTypes] = useState([]);
  const [selectedIndustryTypes, setSelectedIndustryTypes] = useState([]);
  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    standardTypeId: "",
    description: "",
  });
  const isEdit = Boolean(detailId);

  useEffect(() => {
    showLoading(true);
    Promise.all([
      getStandardTypes(),
      getClientTypes(),
      getWorkTypes(),
      getIndustryTypes(),
      detailId ? getChecklistDetail(detailId) : Promise.resolve(null),
    ])
      .then(([standards, clientTypes, workTypes, industryTypes, checklist]) => {
        setStandardList(standards?.data || []);
        setClientTypeOptions(asOptions(clientTypes?.data || []));
        setWorkTypeOptions(asOptions(workTypes?.data || []));
        setIndustryTypeOptions(asOptions(industryTypes?.data || []));
        if (checklist?.status) {
          const data = checklist.data;
          setFormData({
            id: data.id,
            name: data.name || "",
            standardTypeId: data.standardTypeId || "",
            description: data.description || "",
          });
          setSelectedClientTypes(
            selectedOptions(data.clientTypes, "clientType")
          );
          setSelectedWorkTypes(selectedOptions(data.workTypes, "workType"));
          setSelectedIndustryTypes(
            selectedOptions(data.industryTypes, "industryType")
          );
        } else if (checklist && !checklist.status) {
          ErrorNotify(checklist.message);
        }
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load the checklist details.");
      })
      .finally(() => showLoading(false));
  }, [detailId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: formData,
    validationSchema: Yup.object({
      name: Yup.string().trim().required("Enter a checklist name"),
      standardTypeId: Yup.string().required("Select a standard"),
    }),
    onSubmit: (values) => {
      const request = {
        name: values.name.trim(),
        standardTypeId: Number(values.standardTypeId),
        description: values.description?.trim() || "",
        clientTypes: selectedClientTypes.map((item) => item.value),
        industryTypes: selectedIndustryTypes.map((item) => item.value),
        workTypes: selectedWorkTypes.map((item) => item.value),
      };
      if (values.id) request.id = values.id;
      showLoading(true);
      (values.id ? updateChecklist(request) : createChecklist(request))
        .then((response) => {
          if (response.status) {
            SuccessNotify(
              values.id
                ? "Checklist updated successfully."
                : "Checklist created successfully."
            );
            closeAction?.();
          } else {
            ErrorNotify(response.message);
          }
        })
        .catch((error) => {
          console.error(error);
          ErrorNotify("Unable to save the checklist.");
        })
        .finally(() => showLoading(false));
    },
  });

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderColor: "#d7e0ec",
      boxShadow: "none",
      minHeight: 38,
      fontSize: 13,
    }),
    placeholder: (base) => ({ ...base, color: "#8291aa" }),
    multiValue: (base) => ({ ...base, fontSize: 12 }),
  };

  return (
    <Form
      className="standard-drawer-form"
      onSubmit={(event) => {
        event.preventDefault();
        validation.handleSubmit();
      }}
    >
      <div className="standard-drawer-form__content">
        <p className="standard-drawer-form__intro">
          {isEdit
            ? "Update this checklist and the inspection contexts where it can be used."
            : "Create a checklist and choose the inspection contexts where it can be used."}
        </p>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-list-check" />
              Checklist Information
            </span>
            <i className="bx bx-chevron-down" />
          </div>
          <FormGroup>
            <Label htmlFor="checklist-name">
              Checklist Name <b>*</b>
            </Label>
            <Input
              id="checklist-name"
              name="name"
              placeholder="Enter checklist name"
              value={validation.values.name}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.name && validation.errors.name
              )}
            />
            {validation.touched.name && validation.errors.name && (
              <FormFeedback>{validation.errors.name}</FormFeedback>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="checklist-standard">
              Standard <b>*</b>
            </Label>
            <Input
              id="checklist-standard"
              name="standardTypeId"
              type="select"
              value={validation.values.standardTypeId}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.standardTypeId &&
                  validation.errors.standardTypeId
              )}
            >
              <option value="">Select standard</option>
              {standardList.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Input>
            {validation.touched.standardTypeId &&
              validation.errors.standardTypeId && (
                <FormFeedback>{validation.errors.standardTypeId}</FormFeedback>
              )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="checklist-description">Description</Label>
            <Input
              id="checklist-description"
              name="description"
              type="textarea"
              rows="5"
              maxLength="500"
              placeholder="Describe the purpose and scope of this checklist"
              value={validation.values.description}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            <div className="standard-drawer-form__count">
              {validation.values.description.length} / 500
            </div>
          </FormGroup>
        </section>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-slider-alt" />
              Applicability
            </span>
            <i className="bx bx-chevron-down" />
          </div>
          <FormGroup>
            <Label>Client Types</Label>
            <Select
              isMulti
              options={clientTypeOptions}
              value={selectedClientTypes}
              onChange={(values) => setSelectedClientTypes(values || [])}
              placeholder="Select client types"
              styles={selectStyles}
            />
          </FormGroup>
          <FormGroup>
            <Label>Industry Types</Label>
            <Select
              isMulti
              options={industryTypeOptions}
              value={selectedIndustryTypes}
              onChange={(values) => setSelectedIndustryTypes(values || [])}
              placeholder="Select industry types"
              styles={selectStyles}
            />
          </FormGroup>
          <FormGroup>
            <Label>Work Types</Label>
            <Select
              isMulti
              options={workTypeOptions}
              value={selectedWorkTypes}
              onChange={(values) => setSelectedWorkTypes(values || [])}
              placeholder="Select work types"
              styles={selectStyles}
            />
          </FormGroup>
        </section>
        <section className="standard-drawer-form__section standard-drawer-form__section--info">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-info-circle" />
              What happens next
            </span>
          </div>
          <div className="standard-drawer-form__next-steps">
            <strong>After saving, you can</strong>
            <span>
              <i className="bx bx-check" />
              Add questions and checklist activities
            </span>
            <span>
              <i className="bx bx-check" />
              Use the checklist in inspection cycles
            </span>
            <span>
              <i className="bx bx-check" />
              Update its applicability at any time
            </span>
          </div>
        </section>
      </div>
      <footer className="standard-drawer-form__actions">
        <Button
          className="standard-drawer-form__cancel"
          type="button"
          onClick={closeAction}
        >
          Cancel
        </Button>
        <Button className="standard-drawer-form__submit" type="submit">
          {isEdit ? "Save Changes" : "Create Checklist"}
        </Button>
      </footer>
    </Form>
  );
};

export default ChecklistDetail;
